import { refreshDashboardData } from "apps/myExperience/context/dashboard/dataloader";
import { useQueryClient } from "react-query";
import { useCopies } from "shared/hooks/useCopies";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import ApiPurchaseOrder from "shared/model/ApiPurchaseOrder";
import { successToast } from "shared/utils/toastNotification";
import { useGetAdditionalChargesList } from "./dataloader";
import { AdditionalChargeInterface } from "./types";
import React from "react";
import { shouldRefreshPageOnError } from "shared/utils/network";
import { ApiPayForPurchaseOrderResponse } from "shared/types/ApiPayForPurchaseOrderResponse";

export function useAdditionalChargeService(id: string): AdditionalChargeInterface {
  const queryClient = useQueryClient();
  const { data: charges = [], refetch } = useGetAdditionalChargesList(id);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = React.useState<ApiPurchaseOrder | null>(null);
  const navigation = useNavigateWrapper();
  const copies = useCopies();

  const handlePaymentSuccess = React.useCallback(
    async (data: ApiPayForPurchaseOrderResponse) => {
      refreshDashboardData(queryClient, id);
      setSelectedPurchaseOrder(null);
      successToast(
        copies.get(
          data.purchaseOrder.hasRequestedOfflinePayment
            ? "offline.payment.request.submitted.for.approval"
            : "changes.saved"
        )
      );
      if (charges.length === 1) {
        navigation.navigateBack();
      } else {
        await refetch();
      }
    },
    [charges.length, id, navigation, queryClient, refetch, copies]
  );

  const offlineTransactionUpdateSuccessHandler = React.useCallback(async () => {
    setSelectedPurchaseOrder(null);
    successToast(copies.get("offline.payment.request.submitted.for.approval"));
    await refetch();
  }, [refetch, copies]);

  const offlineTransactionCancelSuccessHandler = React.useCallback(async () => {
    setSelectedPurchaseOrder(null);
    await refetch();
  }, [refetch]);

  const invoicePayerPaymentRequestSuccessHandler = React.useCallback(async () => {
    setSelectedPurchaseOrder(null);
    successToast(copies.get("invoice.payment.application.submitted.for.payment"));
    if (charges.length === 1) {
      navigation.navigateBack();
    } else {
      await refetch();
    }
  }, [charges.length, navigation, refetch, copies]);

  const reminderSendSuccessHandler = React.useCallback(async () => {
    setSelectedPurchaseOrder(null);
    successToast(copies.get("invoice.payment.reminder.sent.success"));
    if (charges.length === 1) {
      navigation.navigateBack();
    } else {
      await refetch();
    }
  }, [charges.length, navigation, refetch, copies]);

  const modalCloseHandler = React.useCallback(() => {
    refreshDashboardData(queryClient, id);
    navigation.navigateBack();
  }, [id, navigation, queryClient]);

  const onPurchaseOrderVerificationFailure = React.useCallback(() => {
    setSelectedPurchaseOrder(null);
    refetch();
  }, [refetch]);

  const onPaymentErrorHandler = React.useCallback(
    async err => {
      if (shouldRefreshPageOnError(err)) {
        setSelectedPurchaseOrder(null);
        refetch();
      }
    },
    [refetch]
  );

  return {
    charges,
    selectedPurchaseOrder,
    setSelectedPurchaseOrder,
    handlePaymentSuccess,
    modalCloseHandler,
    onPurchaseOrderVerificationFailure,
    onPaymentErrorHandler,
    reminderSendSuccessHandler,
    offlineTransactionUpdateSuccessHandler,
    invoicePayerPaymentRequestSuccessHandler,
    offlineTransactionCancelSuccessHandler
  };
}
